import * as stylex from '@stylexjs/stylex';
import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setShowModal } from '@/core/hooks/useModalManager.hook';
import { ReleaseNotesModal } from '@/header/ReleaseNotesModal';
import { headerStyles as styles } from '@/header/header.styles';
import { HelpCenterModal } from '@/helpCenter/HelpCenterModal';
import { CopyBranchCode } from '@/homescreen/CopyBranchCode.atom';
import { TrainingPromptModal } from '@/homescreen/TrainingPromptModal.molecule';
import { setTrainingPromptDisplay } from '@/homescreen/homescreen.actions';
import { TrainingPromptDisplay } from '@/homescreen/homescreen.constants';
import { useHomeScreenBranchCode, useHomeScreenTrainingPromptDisplay } from '@/homescreen/homescreen.store';
import { LICENSE_STATUS } from '@/licenseManagement/licenseManagement.constants';
import { useLicenseStatus, useShowOfficeHours } from '@/licenseManagement/licenseManagement.store';
import { APPSERVER_API_PREFIX } from '@/main/app.constants';
import {
  cancelAllServerRequests as cancelAllServerRequestsUtils,
  cancelCurrentUserServerRequests as cancelCurrentUserServerRequestsUtils,
} from '@/requests/pendingRequests.utilities';
import {
  canReadAuditTrail,
  canReadVersionLogs,
  hasAnalyticsAdminCapability,
  isAdmin,
  isTrustedUser,
  showAdministrationMenuItem,
} from '@/services/authorization.service';
import { emitAllServerRequestsCanceled } from '@/services/notifier.service';
import {
  isLearnModeEnabled,
  isNewHomeScreen2025Enabled,
  releaseNotesEnabled,
} from '@/services/systemConfiguration.utilities';
import { TRACK } from '@/track/track.constants';
import { doTrack } from '@/track/track.service';
import { TRAINING_TELEMETRY_HOST } from '@/utilities/externalLinks';
import { dismissHelpCenterBadge, getHelpCenterBadgeInitialState } from '@/utilities/helpCenter.utilities';
import { successToast, warnToast } from '@/utilities/toast.utilities';
import { faThList } from '@seeqdev/icons/fa-pro-solid-svg-icons';
import {
  faBan,
  faBars,
  faCode,
  faFileLines,
  faFileZipper,
  faLock,
  faMemoCircleInfo,
  faQuestionCircle,
  faUserXmark,
} from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { fcSupport, fcTraining, fcVersionLog } from '@seeqdev/icons/seeq';
import { Badge, ExternalLink, Icon, TriggerWithDropdown } from '@seeqdev/qomponents';
import type { IconDefinition } from '@seeqdev/icons';

interface GenerateDropdownEntryOptions {
  icon?: IconDefinition;
  translationKey?: string;
  action?: () => void;
  divider?: boolean;
  testId?: string;
  badge?: string;
}

export const HamburgerMenu = () => {
  const [showReleaseNotes, setShowReleaseNotes] = useState(false);
  const [showHelpCenterBadge, setShowHelpCenterBadge] = useState(getHelpCenterBadgeInitialState);
  const trainingPromptDisplay: TrainingPromptDisplay = useHomeScreenTrainingPromptDisplay();
  const branchCode = useHomeScreenBranchCode();
  const showOfficeHours = useShowOfficeHours();
  const showTrainingModal = trainingPromptDisplay === TrainingPromptDisplay.MODAL;
  const isTrainingVisibleOnHomeScreen = trainingPromptDisplay === TrainingPromptDisplay.HOME_SCREEN;

  const { t } = useTranslation();
  const licenseStatus = useLicenseStatus();
  const accessibleWithCurrentLicense = !_.includes(
    [LICENSE_STATUS.NONE, LICENSE_STATUS.UNKNOWN, LICENSE_STATUS.EXPIRED],
    licenseStatus,
  );

  const cancelCurrentUserServerRequests = () =>
    cancelCurrentUserServerRequestsUtils()
      .then(() => {
        successToast({ messageKey: 'REQUEST_CANCELLATION.SUCCESS_USER' });
      })
      .catch((error) => {
        warnToast({ messageKey: 'REQUEST_CANCELLATION.FAILURE' });
        return error;
      });

  const cancelAllServerRequests = () =>
    cancelAllServerRequestsUtils()
      .then(emitAllServerRequestsCanceled)
      .then(() => {
        successToast({ messageKey: 'REQUEST_CANCELLATION.SUCCESS_ALL' });
      })
      .catch((error) => {
        warnToast({ messageKey: 'REQUEST_CANCELLATION.FAILURE' });
        return error;
      });

  const openAdminTools = () => {
    doTrack(TRACK.MENU, 'Administration', 'open');
    window.open('/administration', 'SeeqAdmin');
  };

  const openApiReference = () => {
    doTrack(TRACK.MENU, 'API Reference', 'open');
    const swaggerUrl = `/api-docs/?baseUrl=${window.encodeURIComponent(`${APPSERVER_API_PREFIX}/swagger.json`)}`;
    window.open(swaggerUrl);
  };

  const handleHelpCenterClick = () => {
    doTrack(TRACK.MENU, 'Help Center', 'open');
    setShowModal(HelpCenterModal, true);

    if (showHelpCenterBadge) {
      dismissHelpCenterBadge();
      setShowHelpCenterBadge(false);
    }
  };

  const generateInformationalEntry = (element: React.ReactNode) => ({
    key: `custom_${Date.now()}`,
    isLabel: true,
    display: true,
    customRender: element,
  });

  const generateDropdownEntry = ({
    icon,
    translationKey = '',
    action = _.noop,
    divider = false,
    testId = '',
    badge,
  }: GenerateDropdownEntryOptions) => {
    const label = badge ? (
      <>
        {t(translationKey)}
        <Badge extraClassNames={stylex.props(styles.trainingBadge).className}>{t(badge)}</Badge>
      </>
    ) : (
      t(translationKey)
    );

    return {
      key: `dropdown_${translationKey}`,
      icon,
      itemXstyle: styles.menuItem,
      labelXstyle: styles.menuItem,
      labelTestId: `moreActions_${translationKey}`,
      iconTestId: `moreActionsIcon_${translationKey}`,
      onClick: action,
      label,
      hasDivider: divider,
      testId,
    };
  };

  const TrainingSteps = (
    <ol {...stylex.props(styles.trainingSteps)} data-testid="training-steps-list">
      {!isLearnModeEnabled() && (
        <li>
          {branchCode ? (
            <CopyBranchCode text="HOME_SCREEN.TRAINING.BRANCH_CODE" branchCode={branchCode} />
          ) : (
            t('HOME_SCREEN.TRAINING.ASK_ADMIN_BRANCH_CODE')
          )}
        </li>
      )}
      <li>
        <ExternalLink
          label={isLearnModeEnabled() ? t('HOME_SCREEN.TRAINING.GO_TO') : t('HOME_SCREEN.TRAINING.REGISTER_ON')}
          href={TRAINING_TELEMETRY_HOST.href}
          onClick={() => doTrack(TRACK.MENU, TRAINING_TELEMETRY_HOST.label, 'open')}>
          {TRAINING_TELEMETRY_HOST.label}
        </ExternalLink>
      </li>
      <li>
        {isTrainingVisibleOnHomeScreen ? (
          <>
            {t('HOME_SCREEN.TRAINING.VISIBILITY_VISIBLE_ON_HOME_SCREEN_TEXT')}{' '}
            <span
              {...stylex.props(styles.trainingLink)}
              data-testid="trainingHomeScreenVisibilityToggle"
              onClick={() => {
                doTrack(TRACK.MENU, 'Training', 'hide from home screen');
                setTrainingPromptDisplay(TrainingPromptDisplay.NONE);
              }}>
              {t('HOME_SCREEN.TRAINING.VISIBILITY_HIDE_TRAINING_LINK')}
            </span>
          </>
        ) : (
          <>
            {t('HOME_SCREEN.TRAINING.VISIBILITY_HIDDEN_ON_HOME_SCREEN_TEXT')}{' '}
            <span
              {...stylex.props(styles.trainingLink)}
              data-testid="trainingHomeScreenVisibilityToggle"
              onClick={() => {
                doTrack(TRACK.MENU, 'Training', 'show on home screen');
                setTrainingPromptDisplay(TrainingPromptDisplay.HOME_SCREEN);
              }}>
              {t('HOME_SCREEN.TRAINING.VISIBILITY_SHOW_TRAINING_LINK')}
            </span>
          </>
        )}
      </li>
    </ol>
  );

  const actions = _.compact([
    {
      icon: faQuestionCircle,
      translationKey: 'HELP_CENTER.TITLE',
      action: handleHelpCenterClick,
      display: accessibleWithCurrentLicense,
      testId: 'supportRequest',
      divider: !releaseNotesEnabled(),
      badge: showHelpCenterBadge ? 'HOME_SCREEN.NEW' : undefined,
    },
    releaseNotesEnabled()
      ? {
          href: '#',
          icon: faMemoCircleInfo,
          translationKey: 'RELEASE_NOTES.TITLE',
          action: () => {
            setShowReleaseNotes(true);
            doTrack(TRACK.MENU, 'Release Notes', 'open');
          },
          display: accessibleWithCurrentLicense,
          testId: 'releaseNotes',
          divider: true,
        }
      : null,
    {
      icon: fcTraining,
      translationKey: 'HOME_SCREEN.TRAINING_CERTIFICATION',
      action: () => {
        doTrack(TRACK.MENU, 'Training Certification', 'open');
        if (isLearnModeEnabled()) {
          window.open('https://training.seeq.com');
        } else {
          window.open(TRAINING_TELEMETRY_HOST.href);
        }
      },
      display: isTrustedUser(),
      divider: !isNewHomeScreen2025Enabled() && !showOfficeHours,
      testId: 'trainingCertification',
    },
    isNewHomeScreen2025Enabled() && isTrustedUser() ? generateInformationalEntry(TrainingSteps) : null,
    {
      icon: fcSupport,
      translationKey: 'HOME_SCREEN.OFFICE_HOURS',
      action: () => {
        doTrack(TRACK.MENU, 'Office Hours', 'open');
        window.open('https://telemetry.seeq.com/office-hours');
      },
      display: showOfficeHours && isTrustedUser(),
      testId: 'officeHours',
      divider: true,
    },
    {
      icon: faLock,
      translationKey: 'ADMINISTRATION',
      action: openAdminTools,
      display: showAdministrationMenuItem() && accessibleWithCurrentLicense,
      testId: 'administration',
    },
    {
      icon: faFileZipper,
      translationKey: 'LICENSE.LICENSE_MANAGEMENT',
      display: isAdmin(),
      testId: 'licenseManagement',
      action: () => {
        doTrack(TRACK.MENU, 'License Management', 'open');
        window.open('/license');
      },
    },
    {
      icon: faFileLines,
      translationKey: 'LOGS.VIEW_LOGS',
      display: accessibleWithCurrentLicense && isTrustedUser(),
      testId: 'viewLogs',
      action: () => {
        doTrack(TRACK.MENU, 'View Logs', 'open');
        window.open('/logs');
      },
      divider: true,
    },
    {
      icon: fcVersionLog,
      translationKey: 'ACCESS_CONTROL.LOCKING.VERSIONING.VERSION_LOG',
      display: canReadVersionLogs(),
      testId: 'versionLog',
      action: () => {
        doTrack(TRACK.MENU, 'Version Log', 'open');
        window.open('/version-log', 'SeeqVersionLog');
      },
    },
    {
      icon: faThList,
      translationKey: 'AUDIT_TRAIL.VIEW_ENTRIES',
      display: canReadAuditTrail() && isTrustedUser(),
      testId: 'viewEntries',
      action: () => {
        doTrack(TRACK.MENU, 'Audit Trail', 'open');
        window.open('/auditTrail');
      },
    },
    {
      icon: faCode,
      translationKey: 'API_REFERENCE',
      action: openApiReference,
      display: isAdmin(),
      testId: 'apiReference',
      divider: true,
    },
    {
      icon: faUserXmark,
      translationKey: 'REQUEST_CANCELLATION.CANCEL_USER',
      action: () => {
        doTrack(TRACK.MENU, 'Cancel User Requests', 'click');
        cancelCurrentUserServerRequests();
      },
      display: true,
      testId: 'requestCancellationUser',
    },
    {
      icon: faBan,
      translationKey: 'REQUEST_CANCELLATION.CANCEL_ALL',
      action: () => {
        doTrack(TRACK.MENU, 'Cancel All Requests', 'click');
        cancelAllServerRequests();
      },
      display: hasAnalyticsAdminCapability(),
      testId: 'requestCancellationAll',
    },
  ]);

  return (
    <>
      <TriggerWithDropdown
        id="hamburger-menu"
        containerTestId="hamburgerMenuDropdown"
        keepFocusInsideDropdown={false}
        triggerIcon={
          <Icon
            icon={faBars}
            xstyle={[styles.navigationIcon, styles.hamburgerIcon, styles.navbarAction]}
            type="white"
            testId="hamburgerMenu"
            id="options"
          />
        }
        dropdownItems={_.filter(actions, (action) => action.display).map((action) => {
          if ('customRender' in action) {
            return { ...action, label: action.customRender, onClick: _.noop };
          }
          return generateDropdownEntry(action);
        })}
      />
      {showReleaseNotes && <ReleaseNotesModal onClose={() => setShowReleaseNotes(false)} />}
      {showTrainingModal && (
        <TrainingPromptModal onClose={() => setTrainingPromptDisplay(TrainingPromptDisplay.NONE)} />
      )}
    </>
  );
};
